import features from "@/configs/features";
import i18n from "@/plugins/i18n";
import axios from "@/axios";

import formatError from "./formatError";
import { defineStore } from "pinia";
import { useUserStore } from "./user";

const access = (obj, property) => {
  const properties = property.split(".");
  let finalObj = obj;
  for (let i = 0; i < properties.length; i++) {
    if (!finalObj) {
      return undefined;
    }
    finalObj = finalObj[properties[i]];
  }
  return finalObj;
};

const priceFormat = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
});
const formatTownName = ({ town, departement, insee }) =>
  features.DEPART && features.INSEE
    ? `${town}\xa0(${departement || insee})`
    : town;

const formatFunctions = {
  towns: (val) => val.map(formatTownName).join(", "),
  date: (val) => {
    if (!val) {
      return i18n.global.t("errors.no_date");
    }
    const d = new Date(val);
    const options = { year: "numeric", month: "numeric", day: "numeric" };
    return d.toLocaleString("fr-FR", options);
  },
  newTowns: (newTowns, { towns_data: towns }) => ({
    list: newTowns.map((newTown) => {
      const isNew = towns.findIndex(({ id }) => id === newTown.id) === -1;
      return {
        id: newTown.id,
        text: `${isNew ? "+" : "-"}\xa0${formatTownName(newTown)}`,
        classes: isNew ? "text-airbnbBlue" : "text-airbnbRed",
      };
    }),
  }),
  price: (val) => priceFormat.format(val),
};

const formatForGrid = (data, cols, idProperty = "id") =>
  data.map((element) => {
    const id = access(element, idProperty);
    const values = cols.map((col) => {
      const val = access(element, col.id);
      if (val === undefined) {
        return "";
      }
      if (col.format) {
        return formatFunctions[col.format](val, element);
      }
      return val;
    });
    return { id, values };
  });

const defaultResourceState = {
  data: [],
  elements: {
    from: null,
    to: null,
    total: null,
    perPage: 10,
  },
  sort: {
    index: null,
    order: 0,
  },
  filter: undefined,
  filterFields: [],
};

const getUrlFromType = (type) => {
  const userStore = useUserStore();
  if (type === "towns") return "/api/towndatas/";
  if (type === "monitoringsuccess") return "/api/importhistories/SUCCESS";
  if (type === "monitoringerror") return "/api/importhistories/ERROR";
  if (type === "imports") return "/api/importdatas/";
  if (type === "mairie") return `/api/importdatas/${userStore.id}`;
  if (type === "epci") return "/api/epci/";
  return `/api/users/byRole/${type.toUpperCase()}`;
};

const filename_from_content_disposition = ( headers ) => {
  // Parse the Content-Disposition header to extract the filename
  const contentDisposition = headers.get("content-disposition");
  const filename_regex = new RegExp("filename=\"(?<filename>.*)\"");
  const found = contentDisposition.match(filename_regex);

  return found.groups.filename;
};

const download_this_payload = (filename, data) => {
  const url = URL.createObjectURL(new File([data], filename));

  const download_button = document.createElement("a");
  download_button.href = url;
  download_button.download = filename;
  download_button.click();

  download_button.remove();
  URL.revokeObjectURL(url);
};


export const useResourcesStore = defineStore("resources", {
  state: () => ({
    waiting: {
      cols: [
        { id: "towns_data", label: "components.grid.town", format: "towns" },
        { id: "lastname", label: "components.grid.last_name" },
        { id: "firstname", label: "components.grid.first_name" },
        {
          id: "email",
          label: "components.grid.email",
          placeholder: "exemple.exemple@exemple.fr",
        },
        {
          id: "phone",
          label: "components.grid.phone",
          placeholder: "00 00 00 00 00",
        },
        {
          id: "created_at",
          label: "components.grid.created_at",
          format: "date",
        },
      ],
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
    },
    updated: {
      cols: [
        { id: "lastname", label: "components.grid.last_name" },
        { id: "firstname", label: "components.grid.first_name" },
        {
          id: "email",
          label: "components.grid.email",
          placeholder: "exemple.exemple@exemple.fr",
        },
        { id: "towns_data", label: "components.grid.town", format: "towns" },
        {
          id: "new_towns",
          label: "components.grid.modifications",
          format: "newTowns",
        },
        {
          id: "phone",
          label: "components.grid.phone",
          placeholder: "00 00 00 00 00",
        },
        {
          id: "last_logged_at",
          label: "components.grid.last_logged_at",
          format: "date",
        },
      ],
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
    },
    user: {
      cols: [
        { id: "lastname", label: "components.grid.last_name" },
        { id: "firstname", label: "components.grid.first_name" },
        {
          id: "email",
          placeholder: "exemple.exemple@exemple.fr",
          label: "components.grid.email",
        },
        { id: "towns_data", label: "components.grid.town", format: "towns" },
        {
          id: "phone",
          label: "components.grid.phone",
          placeholder: "00 00 00 00 00",
        },
        {
          id: "created_at",
          label: "components.grid.created_at",
          format: "date",
        },
        {
          id: "last_logged_at",
          label: "components.grid.last_logged_at",
          format: "date",
        },
      ],
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
    },
    admin: {
      cols: [
        { id: "lastname", label: "components.grid.last_name" },
        { id: "firstname", label: "components.grid.first_name" },
        {
          id: "email",
          label: "components.grid.email",
          placeholder: "exemple.exemple@exemple.fr",
        },
        {
          id: "created_at",
          label: "components.grid.created_at",
          format: "date",
        },
        {
          id: "last_logged_at",
          label: "components.grid.last_logged_at",
          format: "date",
        },
      ],
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
    },
    towns: {
      cols: [
        { id: "town", label: "components.grid.town" },
        ...(features.DEPART
          ? [{ id: "departement", label: "components.grid.department" }]
          : []),
        ...(features.INSEE ? [{ id: "insee", label: "insee" }] : []),
      ],
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
    },
    monitoringsuccess: {
      cols: [
        { id: "import_data.filename", label: "components.grid.file" },
        { id: "message" },
        {
          id: "created_at",
          label: "components.grid.created_at",
          placeholder: "2000-01-01",
          format: "date",
        },
      ],
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
    },
    monitoringerror: {
      cols: [
        { id: "import_data.filename", label: "components.grid.file" },
        { id: "message" },
        {
          id: "created_at",
          label: "components.grid.created_at",
          placeholder: "2000-01-01",
          format: "date",
        },
      ],
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
    },
    imports: {
      cols: [
        {
          id: "town",
          label: "components.grid.town",
          placeholder: "Commune",
          filter: "town",
          default: "null",
        },
        {
          id: "total_taxe",
          label: "components.grid.total",
          placeholder: "1111.11",
          format: "price",
        },
        {
          id: "date",
          label: "components.grid.date_of_modifications",
          placeholder: "2000-01-01 00:00:00",
          format: "date",
        },
      ],
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
    },
    mairie: {
      cols: [
        {
          id: "date",
          label: "components.grid.date",
          format: "date",
          filter: false,
        },
        { id: "town", label: "components.grid.town", filter: "town" },
        ...(features.DEPART
          ? [
              {
                id: "departement",
                label: "components.grid.department",
                filter: "departement",
              },
            ]
          : []),
        {
          id: "total_taxe",
          label: "components.grid.total_taxe",
          placeholder: "000€",
          format: "price",
        },
      ],
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
    },
    epci: {
      cols: [], // No columns as epcis aren't displayed in grid format
      page: {
        current: 1,
        last: null,
      },
      ...defaultResourceState,
      elements: {
        from: null,
        to: null,
        total: null,
        perPage: 6, // Set per-page in the initialization as there's no interface to change it, this is p much constant
      },
    },
  }),
  getters: {
    data(state) {
      return (type) => state[type].data;
    },
    gridFormatted: (state) => (type) => {
      const { data, cols, idProperty } = state[type];
      return formatForGrid(data, cols, idProperty);
    },
    fromId() {
      return (type, targetId) =>
        this.data(type).find(({ id }) => id === targetId);
    },
  },
  actions: {
    setData({
      type,
      data,
      meta: {
        current_page: current,
        last_page: last,
        total,
        from,
        to,
        per_page: perPage,
      },
    }) {
      const newState = {
        ...this.$state[type.toLowerCase()],
        data,
        page: {
          current,
          last,
        },
        elements: {
          from,
          to,
          total,
          perPage,
        },
      };
      this.$state[type.toLowerCase()] = newState;
    },

    setPage({ type, page }) {
      this[type.toLowerCase()].page.current = page;
    },

    setPerPage({ type, perPage }) {
      // Note that if perPage is an empty string, newPerPage will be 0
      const newPerPage = Number(perPage);
      if (Number.isNaN(newPerPage)) {
        // Value provided isn't a number, don't use it
        return;
      }
      this.$state[type.toLowerCase()].elements.perPage = newPerPage;
    },

    setFilter({ type, filter }) {
      const newFilter = filter === "" ? undefined : filter;
      this.$state[type.toLowerCase()].filter = newFilter;
    },

    setFilterFields({ type, filterFields }) {
      this.$state[type.toLowerCase()].filterFields = filterFields;
    },

    setSort({ type, index }) {
      let order = 1;
      if (this.$state[type.toLowerCase()].sort.index === index) {
        order = ((this.$state[type.toLowerCase()].sort.order + 2) % 3) - 1;
      }
      this.$state[type.toLowerCase()].sort = {
        index: order === 0 ? null : index,
        order,
      };
    },
    // type: Either towns or type of user (ADMIN, WAITING or USER)
    async refresh({ type }) {
      const url = getUrlFromType(type);
      const params = {};
      // The store object we'll use to reference the target resource
      const storeObject = this.$state[type.toLowerCase()];
      // Get the right page
      params.page = storeObject.page.current;
      // Use the stored filter
      if (storeObject.filter) {
        params.search = storeObject.filter;
        if (storeObject.filterFields.length > 0) {
          params.col = storeObject.filterFields.join(",");
        }
      }
      // Get the right amount of items per page
      params.per_page = storeObject.elements.perPage;
      if (storeObject.sort.index !== null) {
        if (storeObject.sort.order === 1) {
          params.sortDesc = storeObject.cols[storeObject.sort.index].id;
        } else if (storeObject.sort.order === -1) {
          params.sortAsc = storeObject.cols[storeObject.sort.index].id;
        }
      }
      try {
        const {
          status,
          data: { data, meta },
        } = await axios.get(url, { params });
        if (status === 200) {
          if (params.per_page === 0) {
            this.setData({
              data,
              type,
              meta: {
                current_page: 1,
                last_page: 1,
                total: data.length,
                from: 1,
                to: data.length,
                per_page: 0,
              },
            });
          } else {
            this.setData({
              data,
              type,
              meta,
            });
          }
          return null;
        }
        throw formatError(`Unsupported status code: ${status}`, status);
      } catch (e) {
        if (e.response) {
          return e.response;
        }
        return null;
      }
    },

    // Set the page for the specified resource, and get the resource
    async page({ type, page }) {
      this.setPage({ type, page });
      return this.refresh({ type });
    },

    async perPage({ type, perPage }) {
      if (perPage === "") return null;
      this.setPerPage({ type, perPage });
      return this.refresh({ type });
    },

    // Set the filter for the specified resource, and get the resource
    async filter({ type, filter }) {
      this.setFilter({ type, filter });
      return this.refresh({ type });
    },

    async filterFields({ type, filterFields }) {
      this.setFilterFields({ type, filterFields });
      return this.refresh({ type });
    },

    async sort({ type, index }) {
      this.setSort({ type, index });
      return this.refresh({ type });
    },

    async updateAccount({ id, data, type }) {
      try {
        await axios.put(`/api/users/${id}`, data);
      } catch (e) {
        return e.response;
      }
      return this.refresh({ type });
    },

    async approveAccount({ id }) {
      try {
        await axios.put(`/api/users/${id}/approve`);
        return this.refresh({ type: "waiting" });
      } catch (e) {
        return e.response;
      }
    },

    async deleteAccount({ id, type }) {
      try {
        await axios.delete(`/api/users/${id}`);
      } catch (e) {
        return e.response;
      }
      return this.refresh({ type });
    },

    async promoteAccount({ id }) {
      try {
        await axios.put(`/api/users/${id}`, { role: "Admin" });
      } catch (e) {
        return e.response;
      }
      return this.refresh({ type: "waiting" });
    },

    async updateTown({ id, data }) {
      try {
        await axios.put(`/api/towndatas/${id}`, data);
      } catch (e) {
        return e.response;
      }
      return this.refresh({ type: "towns" });
    },
    async downloadTownFile({ import_data_id }) {
      try {
        const response = await axios({
          url: `/api/towndatas/file/${import_data_id}`,
          method: "GET",
          responseType: "blob",
        });
        let filename = filename_from_content_disposition(response.headers);
        download_this_payload(filename, response.data);
      } catch (e) {
        return e.response;
      }
    },
    async adminDownloadTownFile({ import_data_id }) {
      try {
        const response = await axios({
          url: `/api/importdatas/file/${import_data_id}`,
          method: "GET",
          responseType: "blob",
        });

        let filename = filename_from_content_disposition(response.headers);
        download_this_payload(filename, response.data);
      } catch (e) {
        return e.response;
      }
    },

    async acceptUpdate({ id }) {
      try {
        await axios.put(`/api/users/${id}/approve_towns`);
        return this.refresh({ type: "updated" });
      } catch (e) {
        return e.response;
      }
    },

    async rejectUpdate({ id }) {
      try {
        await axios.put(`/api/users/${id}/reject_towns`);
        return this.refresh({ type: "updated" });
      } catch (e) {
        return e.response;
      }
    },
  },
});
